// ============================================================================
// OBT F2-A — Kampanya API katmanı (paket-yerel)
// ============================================================================
// Mevcut paylaşılan istemci (src/api/client.js) üzerinden çalışır. Yeni axios
// instance KURULMAZ. Kanonik `/api` prefix'i kullanılır; `/api/v1` fallback
// KURULMAZ (backend her ikisini de mount eder, uygulama /api tercih eder).
//
// Ortak `obtApi.js` DEĞİŞTİRİLMEZ (F2-B çakışma önlemi); F2-A kendi
// çağrılarını burada tutar.
//
// Backend sözleşmesi (doğrulanmış — backend/modules/obt/api.py, schemas.py):
//   POST   /obt/campaigns                       → {campaign_id, status, version}
//   PATCH  /obt/campaigns/{id}                  → {campaign_id, version}
//   GET    /obt/campaigns                       → {page, items}
//   GET    /obt/campaigns/{id}                  → CampaignHistoryOut
//   POST   /obt/campaigns/{id}/questions        → {question_id, display_order}
//   GET    /obt/campaigns/{id}/questions        → DraftQuestionsOut (F2 enabler)
//   PUT    /obt/campaigns/{id}/questions        → DraftQuestionsOut (atomik replace)
//   POST   /obt/campaigns/{id}/publication-preview → PublicationValidationResult
//   POST   /obt/campaigns/{id}/publish          → PublicationResult
//   POST   /obt/campaigns/{id}/extend-due        → {campaign_id, response_due_at}
//   GET    /organizasyon/chains/child/{id}      → {root_personnel_id, chain[], count}
// ============================================================================

import apiClient from '@/api/client';

const OBT = '/obt';
const ORG = '/organizasyon';

// ---------------------------------------------------------------------------
// Kampanya CRUD
// ---------------------------------------------------------------------------

/** POST /obt/campaigns — taslak oluştur. */
export async function createCampaign(
  { title, description, topic, responseDueAt },
  { signal } = {}
) {
  const body = { title };
  if (description) body.description = description;
  if (topic) body.topic = topic;
  if (responseDueAt) body.response_due_at = responseDueAt;
  const res = await apiClient.post(`${OBT}/campaigns`, body, { signal });
  return res.data; // {campaign_id, status, version}
}

/**
 * PATCH /obt/campaigns/{id} — taslak meta güncelle (yalnız draft).
 * `expectedVersion` verilirse iyimser kilit uygulanır (uyuşmazlık → 409).
 */
export async function updateCampaign(
  campaignId,
  { title, description, topic, responseDueAt, expectedVersion },
  { signal } = {}
) {
  const body = {};
  if (title !== undefined) body.title = title;
  if (description !== undefined) body.description = description;
  if (topic !== undefined) body.topic = topic;
  if (responseDueAt !== undefined) body.response_due_at = responseDueAt;
  if (typeof expectedVersion === 'number') body.expected_version = expectedVersion;
  const res = await apiClient.patch(
    `${OBT}/campaigns/${encodeURIComponent(campaignId)}`,
    body,
    { signal }
  );
  return res.data; // {campaign_id, version}
}

/** GET /obt/campaigns — kapsam backend'de çözülür (park_id/status/topic sunucu-taraflı).
 * `archived=true` → yalnız arşivlenmiş kampanyalar (varsayılan: yalnız aktif). */
export async function listCampaigns(
  { topic, status, onlyLate, archived, deleted, limit, offset } = {},
  { signal } = {}
) {
  const params = {};
  if (topic) params.topic = topic;
  if (status) params.status = status;
  if (onlyLate) params.only_late = true;
  if (archived) params.archived = true;
  if (deleted) params.deleted = true;
  if (typeof limit === 'number') params.limit = limit;
  if (typeof offset === 'number') params.offset = offset;
  const res = await apiClient.get(`${OBT}/campaigns`, { params, signal });
  return res.data; // {page:{limit,offset,total,has_more}, items:[CampaignHistoryOut]}
}

/**
 * POST /obt/campaigns/{id}/archive — kampanyayı ARŞİVLE (geri-alınabilir soft-delete).
 * Yetki backend'de: yayınlayan ya da oluşturan. @returns {Promise<{campaign_id, is_archived}>}
 */
export async function archiveCampaign(campaignId, { signal } = {}) {
  const res = await apiClient.post(
    `${OBT}/campaigns/${encodeURIComponent(campaignId)}/archive`,
    {},
    { signal }
  );
  return res.data;
}

/**
 * POST /obt/campaigns/{id}/withdraw — yayınlanmış (0 cevap) bilgi talebini
 * TASLAĞA geri çek (yeniden düzenlenebilir). Cevap alınmışsa backend reddeder.
 * @returns {Promise<{campaign_id, status}>}
 */
export async function withdrawCampaign(campaignId, { signal } = {}) {
  const res = await apiClient.post(
    `${OBT}/campaigns/${encodeURIComponent(campaignId)}/withdraw`,
    {},
    { signal }
  );
  return res.data;
}

/** POST /obt/campaigns/{id}/restore — arşivlenmiş kampanyayı GERİ AL. */
export async function restoreCampaign(campaignId, { signal } = {}) {
  const res = await apiClient.post(
    `${OBT}/campaigns/${encodeURIComponent(campaignId)}/restore`,
    {},
    { signal }
  );
  return res.data;
}

/**
 * POST /obt/campaigns/{id}/delete — kampanyayı BELGELİ KALICI sil (künye kalır).
 * Yetki backend'de: admin ya da sahibi (yayınlayan∨oluşturan). `reason` ZORUNLU;
 * kim/ne zaman/neden işlem geçmişinde görünür. @returns {Promise<{campaign_id, is_purged}>}
 */
export async function deleteCampaign(campaignId, { reason } = {}, { signal } = {}) {
  const res = await apiClient.post(
    `${OBT}/campaigns/${encodeURIComponent(campaignId)}/delete`,
    { reason },
    { signal }
  );
  return res.data;
}

/**
 * Sistem ayarı: OBT kalıcı (fiziksel) silme AÇIK mı? (admin panelinden yönetilir).
 * Kapalıysa (veya hata) false döner — "Tam Sil" düğmesi gizlenir. GET /api/system-settings.
 */
export async function fetchObtHardDeleteEnabled({ signal } = {}) {
  try {
    const res = await apiClient.get('/system-settings', { signal });
    return Boolean(res.data?.obt_allow_hard_delete);
  } catch {
    return false;
  }
}

/**
 * POST /obt/campaigns/{id}/hard-delete — FİZİKSEL TAM sil (geri dönüş YOK).
 * Backend: `obt_allow_hard_delete` açık + yetki (admin∨sahibi) + kampanya Silinenler'de.
 */
export async function hardDeleteCampaign(campaignId, { signal } = {}) {
  const res = await apiClient.post(
    `${OBT}/campaigns/${encodeURIComponent(campaignId)}/hard-delete`,
    {},
    { signal }
  );
  return res.data;
}

/**
 * GET /obt/campaigns/{id}/export.xlsx — kampanya sonucunu Excel olarak indir.
 * ACL'li blob; object URL burada oluşturulup temizlenir, tarayıcı indirmesini tetikler.
 */
export async function downloadCampaignResultXlsx(campaignId, { title } = {}, { signal } = {}) {
  const res = await apiClient.get(
    `${OBT}/campaigns/${encodeURIComponent(campaignId)}/export.xlsx`,
    { responseType: 'blob', signal }
  );
  const url = URL.createObjectURL(res.data);
  const safe = (title || `bilgi-talebi-${String(campaignId).slice(0, 8)}`)
    .replace(/[^\p{L}\p{N}\-_ ]/gu, '')
    .trim()
    .slice(0, 60) || 'bilgi-talebi';
  const a = document.createElement('a');
  a.href = url;
  a.download = `OBT-${safe}.xlsx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** GET /obt/campaigns/{id} — kapsam dışıysa backend 404 döner. */
export async function getCampaign(campaignId, { signal } = {}) {
  const res = await apiClient.get(
    `${OBT}/campaigns/${encodeURIComponent(campaignId)}`,
    { signal }
  );
  return res.data;
}

/**
 * POST /obt/campaigns/{id}/extend-due — YAYINLANMIŞ kampanyanın cevap son
 * tarihini UZAT. Yalnız `collecting_responses` durumunda ve YALNIZ ileri tarihe
 * izin verilir; yetki backend'de yayın-anı immutable kaydından (yayınlayan ya da
 * oluşturan) çözülür. `new_due_at` builder ile aynı biçimde gönderilir: ham
 * `datetime-local` (naive) değeri — backend `_as_utc` ile yorumlar.
 * @returns {Promise<{campaign_id, response_due_at}>}
 */
export async function extendCampaignDue(campaignId, newDueAt, { signal } = {}) {
  const res = await apiClient.post(
    `${OBT}/campaigns/${encodeURIComponent(campaignId)}/extend-due`,
    { new_due_at: newDueAt },
    { signal }
  );
  return res.data; // {campaign_id, response_due_at}
}

/**
 * GET /obt/campaigns/{id}/respondents — kampanyanın TÜM hedefleri + cevap durumu.
 * `pending`/`late`'ten farkı: sorumlu-park=0 hedefler dahil hiçbiri filtrelenmez.
 * "4 hedef kim?" sorusunu yanıtlar. Kapsam görünürlüğü backend'de çözülür.
 * @returns {Promise<{page, campaign_id, items: Array<{
 *   person:{personnel_id,name,role}, responsible_park_count, answered_park_count,
 *   draft_park_count, untouched_park_count, due_passed, late_by_seconds}>}>}
 */
export async function fetchCampaignRespondents(
  campaignId,
  { limit, offset } = {},
  { signal } = {}
) {
  const params = {};
  if (typeof limit === 'number') params.limit = limit;
  if (typeof offset === 'number') params.offset = offset;
  const res = await apiClient.get(
    `${OBT}/campaigns/${encodeURIComponent(campaignId)}/respondents`,
    { params, signal }
  );
  return res.data; // RespondentStatusPage
}

/**
 * GET /obt/campaigns/{id}/respondents/{personnelId}/responses — bir hedefin bu
 * kampanyadaki TÜM park cevaplarının salt-okunur dosyası (park/soru/cevap/foto).
 * Fotoğraf içeriği ayrı ACL'li uçtan (readResponsePhoto) okunur.
 * @returns {Promise<{campaign_id, person, responses: Array<{
 *   response_id, park_name, status, review_state, submitted_at, response_text,
 *   answers: Array<{question_id,question_text,question_type,answer_boolean,
 *     answer_text,option_values,photo_ids}>, other_photo_ids}>}>}
 */
export async function fetchRespondentDetail(
  campaignId,
  personnelId,
  { signal } = {}
) {
  const res = await apiClient.get(
    `${OBT}/campaigns/${encodeURIComponent(campaignId)}/respondents/${encodeURIComponent(
      personnelId
    )}/responses`,
    { signal }
  );
  return res.data; // RespondentDetailOut
}

// ---------------------------------------------------------------------------
// Sorular — F2 Backend Enabler ile GERİ OKUMA + ATOMİK DEĞİŞTİRME uçları geldi.
// Artık sorular tek tek POST edilmez; taslak soru seti tek atomik PUT ile
// kaydedilir (yarım set oluşmaz) ve taslak yeniden açıldığında GET ile okunur.
// ---------------------------------------------------------------------------

/**
 * GET /obt/campaigns/{id}/questions — kampanyanın CANLI taslak sorularını okur.
 * @returns {Promise<{campaign_id, campaign_status, campaign_version, questions: Array}>}
 */
export async function getCampaignQuestions(campaignId, { signal } = {}) {
  const res = await apiClient.get(
    `${OBT}/campaigns/${encodeURIComponent(campaignId)}/questions`,
    { signal }
  );
  return res.data; // DraftQuestionsOut
}

/**
 * PUT /obt/campaigns/{id}/questions — soru setini ATOMİK olarak baştan yaz.
 * `expectedVersion` verilirse iyimser kilit uygulanır (uyuşmazlık → 409).
 * @param {number|null} expectedVersion
 * @param {Array<object>} questions normalize edilmiş soru girdileri (display_order dahil)
 * @returns {Promise<{campaign_id, campaign_status, campaign_version, questions: Array}>}
 */
export async function replaceCampaignQuestions(
  campaignId,
  { expectedVersion, questions },
  { signal } = {}
) {
  const body = { questions: Array.isArray(questions) ? questions : [] };
  if (typeof expectedVersion === 'number') {
    body.expected_campaign_version = expectedVersion;
  }
  const res = await apiClient.put(
    `${OBT}/campaigns/${encodeURIComponent(campaignId)}/questions`,
    body,
    { signal }
  );
  return res.data; // DraftQuestionsOut (yeni campaign_version dahil)
}

/**
 * POST /obt/campaigns/{id}/questions — tek soru ekle (LEGACY; atomik PUT tercih
 * edilir, yalnız geriye uyumluluk için tutulur).
 */
export async function addQuestion(campaignId, q, { signal } = {}) {
  const body = {
    question_text: q.question_text,
    question_type: q.question_type,
    display_order: q.display_order,
    is_required: q.is_required,
    photo_required: q.photo_required,
  };
  if (q.park_feature_key) body.park_feature_key = q.park_feature_key;
  if (q.filter_configuration) body.filter_configuration = q.filter_configuration;
  if (Array.isArray(q.options) && q.options.length > 0) body.options = q.options;
  const res = await apiClient.post(
    `${OBT}/campaigns/${encodeURIComponent(campaignId)}/questions`,
    body,
    { signal }
  );
  return res.data; // {question_id, display_order}
}

/**
 * POST /obt/campaigns/{id}/publication-preview — yayın öncesi SALT-OKUNUR
 * doğrulama (DB yazmaz). Hedefler backend'de yeniden çözülür.
 * @param {string[]} targetPersonnelIds
 * @returns {Promise<{ok, campaign_id, next_version, question_count, target_count, issues}>}
 */
export async function publicationPreview(
  campaignId,
  targetPersonnelIds,
  { signal } = {}
) {
  const res = await apiClient.post(
    `${OBT}/campaigns/${encodeURIComponent(campaignId)}/publication-preview`,
    { target_personnel_ids: targetPersonnelIds },
    { signal }
  );
  return res.data; // PublicationValidationResult
}

/**
 * POST /obt/campaigns/{id}/publish — hedeflere yayınla.
 * @param {string[]} targetPersonnelIds 1..500 kişi
 */
export async function publishCampaign(campaignId, targetPersonnelIds, { signal } = {}) {
  const res = await apiClient.post(
    `${OBT}/campaigns/${encodeURIComponent(campaignId)}/publish`,
    { target_personnel_ids: targetPersonnelIds },
    { signal }
  );
  return res.data; // PublicationResult
}

// ---------------------------------------------------------------------------
// Hedef seçici kaynağı — organizasyon alt zinciri (isim + role_slug taşır).
// Tüm yayıncı roller `organizasyon.agac_gor` iznine sahiptir (seed 0052).
// ---------------------------------------------------------------------------

/**
 * GET /organizasyon/chains/child/{personnelId} — aktörün alt zinciri.
 * @returns {Promise<Array<{personnel_id, name, role_slug, parent_personnel_id, depth}>>}
 */
export async function fetchChildChain(personnelId, { signal } = {}) {
  const res = await apiClient.get(
    `${ORG}/chains/child/${encodeURIComponent(personnelId)}`,
    { params: { include_self: false }, signal }
  );
  return Array.isArray(res.data?.chain) ? res.data.chain : [];
}
